/**
 * PSSP LABS — "PSSP Precision" theme
 * global.js — core namespace + shared custom elements.
 *
 * Contract (see _build/BUILD-SPEC.md §4/§9):
 * - Vanilla ES2020, no build step, no external dependencies.
 * - The ONLY globals are window.PSSPTheme (this file) and window.PSSPRoutes
 *   (inlined by layout/theme.liquid). Everything else lives on PSSPTheme or
 *   inside customElements definitions (anonymous class expressions).
 * - Every custom element is guarded with customElements.get() and cleans up
 *   all listeners/observers in disconnectedCallback() so the Shopify theme
 *   editor lifecycle (shopify:section:load/unload) is leak-free.
 * - All motion respects: prefers-reduced-motion, body[data-perf="lite"], and
 *   body[data-animations="false"], via PSSPTheme.motionOK().
 *
 * Body data-attributes read here (set in layout/theme.liquid):
 *   data-perf              "full" | "balanced" | "lite"
 *   data-animations        "true" | "false"
 *   data-parallax-enabled  "true" | "false"
 *   data-marquee-enabled   "true" | "false"
 *   data-magnetic          "true" | "false"
 *   data-cursor-glow       "true" | "false"
 */

/* ==========================================================================
   1. PSSPTheme core namespace
   ========================================================================== */

if (!window.PSSPTheme) {
  window.PSSPTheme = {
    /* Live media queries (matches re-evaluates on every call) */
    _mqReduced: window.matchMedia('(prefers-reduced-motion: reduce)'),
    _mqPointerFine: window.matchMedia('(pointer: fine)'),

    /** True when the OS asks for reduced motion (live). */
    prefersReducedMotion() {
      return this._mqReduced.matches;
    },

    /** Merchant performance mode: "full" | "balanced" | "lite". */
    perf() {
      return (document.body && document.body.dataset.perf) || 'full';
    },

    /** Merchant master animation switch (Theme settings → Animation). */
    animationsEnabled() {
      return !document.body || document.body.dataset.animations !== 'false';
    },

    /** Single gate every animation must pass. */
    motionOK() {
      return (
        !this.prefersReducedMotion() &&
        this.perf() !== 'lite' &&
        this.animationsEnabled()
      );
    },

    /** True on mouse/trackpad devices — gate for hover-driven effects. */
    pointerFine() {
      return this._mqPointerFine.matches;
    },

    /* ----- runtime strings (from #ThemeStrings JSON in theme.liquid) ----- */

    _strings: null,

    /** Lazily-parsed localized strings. Always returns an object. */
    get strings() {
      if (this._strings === null) {
        const el = document.getElementById('ThemeStrings');
        try {
          this._strings = el ? JSON.parse(el.textContent) : {};
        } catch (error) {
          this._strings = {};
        }
      }
      return this._strings;
    },

    /* ----- accessibility helpers ----- */

    /**
     * Push a message to the polite live region (#A11yAnnouncer).
     * Cleared first, then set on the next frame so repeated identical
     * messages are re-announced by screen readers.
     */
    announce(message) {
      const region = document.getElementById('A11yAnnouncer');
      if (!region) return;
      region.textContent = '';
      window.requestAnimationFrame(() => {
        region.textContent = message;
      });
    },

    /** All visible focusable descendants of a container. */
    getFocusable(container) {
      const selector = [
        'summary',
        'a[href]',
        'button:not([disabled])',
        '[tabindex]:not([tabindex^="-"])',
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'audio[controls]',
        'video[controls]'
      ].join(', ');
      return Array.prototype.filter.call(
        container.querySelectorAll(selector),
        (el) =>
          el.offsetWidth > 0 ||
          el.offsetHeight > 0 ||
          el.getClientRects().length > 0 ||
          el === document.activeElement
      );
    },

    _trap: null,

    /**
     * Standard focus trap. Only one trap is active at a time; opening a new
     * one releases the previous. Listeners are stored so releaseFocus() can
     * remove them (no leaks).
     * @param {Element} container  Element to trap focus inside.
     * @param {Element} [initial]  Element to focus first (default: first focusable).
     */
    trapFocus(container, initial) {
      this.releaseFocus();

      const onFocusin = (event) => {
        if (container === event.target || container.contains(event.target)) return;
        const focusables = this.getFocusable(container);
        (initial || focusables[0] || container).focus();
      };

      const onKeydown = (event) => {
        if (event.key !== 'Tab') return;
        /* Re-query on every Tab — trapped content can change (e.g. cart). */
        const focusables = this.getFocusable(container);
        if (focusables.length === 0) {
          event.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('focusin', onFocusin);
      document.addEventListener('keydown', onKeydown);
      this._trap = { container, onFocusin, onKeydown };

      const target = initial || this.getFocusable(container)[0] || container;
      if (target === container && !container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: true });
    },

    /** Remove the active focus trap (safe to call when none is active). */
    releaseFocus() {
      if (!this._trap) return;
      document.removeEventListener('focusin', this._trap.onFocusin);
      document.removeEventListener('keydown', this._trap.onKeydown);
      this._trap = null;
    },

    /* ----- scroll locking (shared by drawers + modals, counted) ----- */

    _scrollLocks: 0,

    lockScroll() {
      this._scrollLocks += 1;
      if (this._scrollLocks === 1) {
        document.body.style.overflow = 'hidden';
      }
    },

    unlockScroll() {
      this._scrollLocks = Math.max(0, this._scrollLocks - 1);
      if (this._scrollLocks === 0) {
        document.body.style.overflow = '';
      }
    },

    /* ----- timing utilities ----- */

    /** Trailing-edge debounce. */
    debounce(fn, wait) {
      let timer = null;
      return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    /** Leading throttle with one trailing invoke so the last call lands. */
    throttle(fn, limit) {
      let waiting = false;
      let trailingArgs = null;
      return function throttled(...args) {
        if (waiting) {
          trailingArgs = args;
          return;
        }
        fn.apply(this, args);
        waiting = true;
        setTimeout(() => {
          waiting = false;
          if (trailingArgs) {
            const queued = trailingArgs;
            trailingArgs = null;
            throttled.apply(this, queued);
          }
        }, limit);
      };
    },

    /* ----- network ----- */

    /** Shared fetch() init for Shopify AJAX endpoints (used by cart.js). */
    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/${type}`
        }
      };
    },

    /* ----- one-shot visibility helper ----- */

    /**
     * Run cb(el) once when el nears the viewport. Returns a disconnect
     * function callers MUST invoke from disconnectedCallback.
     */
    onVisible(el, cb, rootMargin = '200px') {
      if (!('IntersectionObserver' in window)) {
        cb(el);
        return function noop() {};
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.disconnect();
            cb(el);
          });
        },
        { rootMargin }
      );
      io.observe(el);
      return function disconnect() {
        io.disconnect();
      };
    }
  };
}

/* ==========================================================================
   2. Scroll-reveal controller
   --------------------------------------------------------------------------
   - [data-reveal]                → .is-revealed on first intersection
   - [data-reveal-delay="1..8"]   → --reveal-delay = n × 60ms (inline var)
   - [data-reveal-group]          → children staggered 60ms apart on intersect
   base.css hides `.js [data-reveal]` / `.js [data-reveal-group] > *` until
   .is-revealed, so with reduced motion / no JS nothing is ever hidden.
   ========================================================================== */

if (!PSSPTheme.reveal) {
  PSSPTheme.reveal = {
    _io: null,

    /** (Re)scan the document. Called at boot and on shopify:section:load. */
    scan() {
      if (!PSSPTheme.motionOK() || !('IntersectionObserver' in window)) {
        this.revealAll();
        return;
      }
      if (!this._io) {
        this._io = new IntersectionObserver(this._onIntersect.bind(this), {
          threshold: 0.12
        });
      }

      document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
        /* Children of a reveal group are staggered by the group instead. */
        if (el.parentElement && el.parentElement.hasAttribute('data-reveal-group')) return;
        const step = parseInt(el.getAttribute('data-reveal-delay'), 10);
        if (step >= 1) {
          el.style.setProperty('--reveal-delay', `${Math.min(step, 8) * 60}ms`);
        }
        this._io.observe(el);
      });

      document.querySelectorAll('[data-reveal-group]:not(.is-revealed)').forEach((group) => {
        this._io.observe(group);
      });
    },

    _onIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this._io.unobserve(entry.target);
        if (entry.target.hasAttribute('data-reveal-group')) {
          this._revealGroup(entry.target);
        } else {
          entry.target.classList.add('is-revealed');
        }
      });
    },

    /** Stagger a group's direct children 60ms apart, then clear the inline
        delays so later hover/focus transitions are not delayed. */
    _revealGroup(group) {
      group.classList.add('is-revealed');
      const children = Array.prototype.slice.call(group.children);
      children.forEach((child, i) => {
        child.style.transitionDelay = `${i * 60}ms`;
        child.classList.add('is-revealed');
      });
      const settle = children.length * 60 + 800;
      setTimeout(() => {
        children.forEach((child) => {
          child.style.transitionDelay = '';
        });
      }, settle);
    },

    /** Instantly reveal everything (reduced motion / lite mode / no IO). */
    revealAll() {
      document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
        el.classList.add('is-revealed');
      });
      document.querySelectorAll('[data-reveal-group]').forEach((group) => {
        group.classList.add('is-revealed');
        Array.prototype.forEach.call(group.children, (child) => {
          child.style.transitionDelay = '';
          child.classList.add('is-revealed');
        });
      });
    }
  };
}

/* ==========================================================================
   3. Parallax controller
   --------------------------------------------------------------------------
   [data-parallax="0.1"] (factor clamped 0–0.25) — element is translated on a
   single rAF scroll loop, based on its center vs the viewport center, so it
   drifts slower than the page. Fully disabled with reduced motion, lite
   perf, or body[data-parallax-enabled="false"]; paused on hidden tabs.
   ========================================================================== */

if (!PSSPTheme.parallax) {
  PSSPTheme.parallax = {
    _items: [],
    _raf: null,
    _active: false,
    _onScroll: null,
    _onResize: null,
    _onVisibility: null,

    enabled() {
      return (
        PSSPTheme.motionOK() &&
        document.body.dataset.parallaxEnabled !== 'false'
      );
    },

    /** (Re)collect [data-parallax] elements. Boot + shopify:section:load. */
    scan() {
      this._items = Array.prototype.map
        .call(document.querySelectorAll('[data-parallax]'), (el) => {
          const raw = parseFloat(el.getAttribute('data-parallax'));
          const factor = Math.min(Math.max(Number.isFinite(raw) ? raw : 0, 0), 0.25);
          return { el, factor, current: 0 };
        })
        .filter((item) => item.factor > 0);

      if (!this.enabled() || this._items.length === 0) {
        this._teardown();
        return;
      }
      this._setup();
      this.update();
    },

    _setup() {
      if (this._active) return;
      this._active = true;

      this._onScroll = () => {
        if (document.hidden || this._raf) return;
        this._raf = window.requestAnimationFrame(() => {
          this._raf = null;
          this.update();
        });
      };
      this._onResize = PSSPTheme.debounce(() => this.update(), 150);
      this._onVisibility = () => {
        if (document.hidden && this._raf) {
          window.cancelAnimationFrame(this._raf);
          this._raf = null;
        } else if (!document.hidden) {
          this.update();
        }
      };

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      document.addEventListener('visibilitychange', this._onVisibility);
    },

    _teardown() {
      if (this._active) {
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onResize);
        document.removeEventListener('visibilitychange', this._onVisibility);
        if (this._raf) {
          window.cancelAnimationFrame(this._raf);
          this._raf = null;
        }
        this._active = false;
      }
      /* Reset any transforms we applied. */
      this._items.forEach((item) => {
        item.el.style.transform = '';
        item.current = 0;
      });
    },

    update() {
      const viewportCenter = window.innerHeight / 2;
      this._items.forEach((item) => {
        if (!item.el.isConnected) return;
        const rect = item.el.getBoundingClientRect();
        /* Subtract the offset we already applied → untransformed center. */
        const naturalCenter = rect.top + rect.height / 2 - item.current;
        /* Off-screen by more than a viewport → skip the write. */
        if (naturalCenter < -window.innerHeight || naturalCenter > window.innerHeight * 2) return;
        const offset = (viewportCenter - naturalCenter) * item.factor;
        if (Math.abs(offset - item.current) < 0.1) return;
        item.current = offset;
        item.el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
      });
    }
  };
}

/* ==========================================================================
   4. <pssp-marquee> — infinite capability ticker
   --------------------------------------------------------------------------
   Markup:  <pssp-marquee data-speed="40" data-direction="left">
              <div class="marquee__track"> …items… </div>
              <button data-marquee-toggle …>(optional pause/play)</button>
            </pssp-marquee>
   Animated mode: track content is cloned once into an aria-hidden
   .marquee__clone wrapper; --marquee-duration = trackWidth / speed (px/s);
   --marquee-direction normal|reverse. .is-paused sets
   animation-play-state: paused (base.css). Offscreen + hidden tab pause.
   Static mode (motion off or data-marquee-enabled="false"): .marquee--static
   — no clone, plain horizontally scrollable row.
   ========================================================================== */

if (!customElements.get('pssp-marquee')) {
  customElements.define(
    'pssp-marquee',
    class extends HTMLElement {
      connectedCallback() {
        this._track = this.querySelector('.marquee__track');
        if (!this._track || this._track.children.length === 0) return;

        this._userPaused = false;
        this._offscreen = false;
        this._toggleBtn = this.querySelector('[data-marquee-toggle]');

        const animate =
          PSSPTheme.motionOK() && document.body.dataset.marqueeEnabled !== 'false';

        if (!animate) {
          /* Static fallback: plain overflow row, no clone, no controls. */
          this.classList.add('marquee--static');
          if (this._toggleBtn) this._toggleBtn.hidden = true;
          return;
        }

        this.classList.remove('marquee--static');
        this._build();

        /* Pause the CSS animation while offscreen. */
        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._offscreen = !entry.isIntersecting;
              this._sync();
            });
          },
          { rootMargin: '50px' }
        );
        this._io.observe(this);

        /* Pause while the tab is hidden. */
        this._onVisibility = () => this._sync();
        document.addEventListener('visibilitychange', this._onVisibility);

        /* Recompute duration when layout width changes. */
        this._onResize = PSSPTheme.debounce(() => this._measure(), 200);
        window.addEventListener('resize', this._onResize);

        /* Optional user pause/play control. */
        if (this._toggleBtn) {
          this._onToggle = () => {
            this._userPaused = !this._userPaused;
            this._sync();
            this._updateToggle();
          };
          this._toggleBtn.addEventListener('click', this._onToggle);
          this._updateToggle();
        }
      }

      disconnectedCallback() {
        if (this._io) this._io.disconnect();
        if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._toggleBtn && this._onToggle) this._toggleBtn.removeEventListener('click', this._onToggle);
        this._io = null;
        this._onVisibility = null;
        this._onResize = null;
        this._onToggle = null;
      }

      /** Clone the track content once (aria-hidden) and set CSS vars. */
      _build() {
        /* Width of the ORIGINAL content, measured before cloning. */
        this._contentWidth = this._track.scrollWidth;

        if (!this._track.querySelector('.marquee__clone')) {
          const clone = document.createElement('span');
          clone.className = 'marquee__clone';
          clone.setAttribute('aria-hidden', 'true');
          Array.prototype.forEach.call(this._track.children, (child) => {
            clone.appendChild(child.cloneNode(true));
          });
          this._track.appendChild(clone);
        }

        this._measure();
        this.style.setProperty(
          '--marquee-direction',
          this.dataset.direction === 'right' ? 'reverse' : 'normal'
        );
      }

      /** duration (s) = content width (px) / speed (px per second). */
      _measure() {
        if (!this.isConnected || !this._track) return;
        const clone = this._track.querySelector('.marquee__clone');
        const width = clone ? clone.offsetWidth : this._contentWidth;
        const speed = Math.max(parseFloat(this.dataset.speed) || 40, 5);
        if (width > 0) {
          this.style.setProperty('--marquee-duration', `${(width / speed).toFixed(2)}s`);
        }
      }

      /** Combine every pause source into the .is-paused class. */
      _sync() {
        const paused = this._userPaused || this._offscreen || document.hidden;
        this.classList.toggle('is-paused', paused);
      }

      /** Swap the toggle button label between pause/play. */
      _updateToggle() {
        const btn = this._toggleBtn;
        if (!btn) return;
        const pauseLabel = btn.dataset.labelPause || PSSPTheme.strings.pause || 'Pause';
        const playLabel = btn.dataset.labelPlay || PSSPTheme.strings.play || 'Play';
        btn.setAttribute('aria-label', this._userPaused ? playLabel : pauseLabel);
        btn.setAttribute('aria-pressed', this._userPaused ? 'true' : 'false');
        btn.classList.toggle('is-paused', this._userPaused);
      }
    }
  );
}

/* ==========================================================================
   5. <pssp-slider> — scroll-snap carousel shell
   --------------------------------------------------------------------------
   Markup (BUILD-SPEC §4):
     <pssp-slider>
       <div class="slider__viewport">
         <ul class="slider__track">
           <li class="slider__slide">…</li> …
         </ul>
       </div>
       <button data-slider-prev>…</button>
       <button data-slider-next>…</button>
       <span class="slider__status"></span>
       <div class="slider__progress"></div>   (reads --progress 0..1)
     </pssp-slider>
   Scrolling/touch/keyboard are native (scroll-snap); JS only tracks state,
   drives the buttons, and reacts to editor block selection.
   ========================================================================== */

if (!customElements.get('pssp-slider')) {
  customElements.define(
    'pssp-slider',
    class extends HTMLElement {
      connectedCallback() {
        this._viewport = this.querySelector('.slider__viewport');
        this._track = this.querySelector('.slider__track');
        if (!this._viewport || !this._track) return;

        this._slides = Array.prototype.slice.call(
          this._track.querySelectorAll('.slider__slide')
        );
        this._prev = this.querySelector('[data-slider-prev]');
        this._next = this.querySelector('[data-slider-next]');
        this._status = this.querySelector('.slider__status');
        this._progress = this.querySelector('.slider__progress');

        /* Accessibility: carousel semantics + per-slide labels. The section
           may pre-set localized attributes in Liquid; only fill gaps. */
        if (!this._viewport.hasAttribute('role')) this._viewport.setAttribute('role', 'group');
        if (!this._viewport.hasAttribute('aria-roledescription')) {
          this._viewport.setAttribute('aria-roledescription', 'carousel');
        }
        if (!this._viewport.hasAttribute('tabindex')) this._viewport.setAttribute('tabindex', '0');
        this._slides.forEach((slide, i) => {
          if (!slide.hasAttribute('role')) slide.setAttribute('role', 'group');
          if (!slide.hasAttribute('aria-label')) {
            slide.setAttribute('aria-label', this._slideLabel(i + 1, this._slides.length));
          }
        });

        /* State tracking: throttled scroll + scrollend when supported. */
        this._onScroll = PSSPTheme.throttle(() => this._update(), 100);
        this._viewport.addEventListener('scroll', this._onScroll, { passive: true });
        if ('onscrollend' in window) {
          this._onScrollEnd = () => this._update();
          this._viewport.addEventListener('scrollend', this._onScrollEnd);
        }

        if (this._prev) {
          this._onPrev = () => this._step(-1);
          this._prev.addEventListener('click', this._onPrev);
        }
        if (this._next) {
          this._onNext = () => this._step(1);
          this._next.addEventListener('click', this._onNext);
        }

        /* Theme editor: bring a selected block's slide into view. */
        this._onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          const slide = event.target.closest('.slider__slide');
          if (!slide) return;
          const left =
            slide.getBoundingClientRect().left -
            this._viewport.getBoundingClientRect().left +
            this._viewport.scrollLeft;
          this._viewport.scrollTo({ left, behavior: 'auto' });
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);

        this._onResize = PSSPTheme.debounce(() => this._update(), 150);
        window.addEventListener('resize', this._onResize);

        this._update();
      }

      disconnectedCallback() {
        if (this._viewport) {
          this._viewport.removeEventListener('scroll', this._onScroll);
          if (this._onScrollEnd) this._viewport.removeEventListener('scrollend', this._onScrollEnd);
        }
        if (this._prev && this._onPrev) this._prev.removeEventListener('click', this._onPrev);
        if (this._next && this._onNext) this._next.removeEventListener('click', this._onNext);
        if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        this._onScroll = null;
        this._onScrollEnd = null;
        this._onPrev = null;
        this._onNext = null;
        this._onBlockSelect = null;
        this._onResize = null;
      }

      /** "Slide n of total" from #ThemeStrings (slideOf), with fallback. */
      _slideLabel(index, total) {
        const pattern = PSSPTheme.strings.slideOf;
        if (pattern) {
          return pattern.replace('[index]', index).replace('[total]', total);
        }
        return `${index} / ${total}`;
      }

      /** Index of the slide closest to the current scroll offset. */
      _currentIndex() {
        const trackLeft = this._track.getBoundingClientRect().left;
        let best = 0;
        let bestDist = Infinity;
        this._slides.forEach((slide, i) => {
          /* Slide offset within the track equals its target scrollLeft
             (the track starts at scroll offset 0). */
          const left = slide.getBoundingClientRect().left - trackLeft;
          const d = Math.abs(left - this._viewport.scrollLeft);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        return best;
      }

      /** Refresh buttons, status counter, and progress bar. */
      _update() {
        if (!this.isConnected || this._slides.length === 0) return;
        const vp = this._viewport;
        const maxScroll = Math.max(vp.scrollWidth - vp.clientWidth, 0);

        if (this._prev) this._prev.disabled = vp.scrollLeft <= 1;
        if (this._next) this._next.disabled = vp.scrollLeft >= maxScroll - 1;

        if (this._status) {
          const index = this._currentIndex();
          this._status.textContent = this._slideLabel(index + 1, this._slides.length);
        }

        if (this._progress) {
          const ratio = maxScroll > 0 ? Math.min(Math.max(vp.scrollLeft / maxScroll, 0), 1) : 1;
          this._progress.style.setProperty('--progress', ratio.toFixed(4));
        }
      }

      /** Scroll by exactly one slide width in the given direction. */
      _step(direction) {
        let amount = this._viewport.clientWidth;
        if (this._slides.length >= 2) {
          const delta =
            this._slides[1].getBoundingClientRect().left -
            this._slides[0].getBoundingClientRect().left;
          if (delta > 0) amount = delta;
        }
        this._viewport.scrollBy({
          left: amount * direction,
          behavior: PSSPTheme.motionOK() ? 'smooth' : 'auto'
        });
      }
    }
  );
}

/* ==========================================================================
   6. <pssp-tabs> — accessible tabs
   --------------------------------------------------------------------------
   Markup: [role=tablist] > button[role=tab][aria-controls=panelId] +
   [role=tabpanel] elements. Roving tabindex, Arrow/Home/End keys, panels
   toggled with the hidden attribute.
   ========================================================================== */

if (!customElements.get('pssp-tabs')) {
  customElements.define(
    'pssp-tabs',
    class extends HTMLElement {
      connectedCallback() {
        this._tablist = this.querySelector('[role="tablist"]');
        this._tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
        if (!this._tablist || this._tabs.length === 0) return;

        this._onClicks = this._tabs.map((tab, i) => {
          const handler = () => this._select(i, false);
          tab.addEventListener('click', handler);
          return handler;
        });

        this._onKeydown = (event) => {
          const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          const current = this._tabs.indexOf(document.activeElement);
          if (current === -1) return;
          event.preventDefault();
          let next = current;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            next = (current + 1) % this._tabs.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            next = (current - 1 + this._tabs.length) % this._tabs.length;
          } else if (event.key === 'Home') {
            next = 0;
          } else if (event.key === 'End') {
            next = this._tabs.length - 1;
          }
          this._select(next, true);
        };
        this._tablist.addEventListener('keydown', this._onKeydown);

        /* Initial state: honor a pre-set aria-selected, else first tab. */
        const preset = this._tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        this._select(preset === -1 ? 0 : preset, false);
      }

      disconnectedCallback() {
        if (this._onClicks) {
          this._tabs.forEach((tab, i) => tab.removeEventListener('click', this._onClicks[i]));
        }
        if (this._tablist && this._onKeydown) {
          this._tablist.removeEventListener('keydown', this._onKeydown);
        }
        this._onClicks = null;
        this._onKeydown = null;
      }

      _panelFor(tab) {
        const id = tab.getAttribute('aria-controls');
        return id ? document.getElementById(id) : null;
      }

      _select(index, focus) {
        this._tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
          const panel = this._panelFor(tab);
          if (panel) panel.hidden = !selected;
        });
        if (focus) this._tabs[index].focus();
      }
    }
  );
}

/* ==========================================================================
   7. <pssp-counter> — count-up stat
   --------------------------------------------------------------------------
   <pssp-counter data-target="128" data-prefix="" data-suffix="+"
                 data-duration="1600">128+</pssp-counter>
   Ease-out-cubic count-up on first intersection; final value rendered
   immediately when motion is off. Locale-aware via Intl.NumberFormat.
   ========================================================================== */

if (!customElements.get('pssp-counter')) {
  customElements.define(
    'pssp-counter',
    class extends HTMLElement {
      connectedCallback() {
        const raw = this.dataset.target;
        this._target = parseFloat(raw);
        if (!Number.isFinite(this._target)) return;

        this._prefix = this.dataset.prefix || '';
        this._suffix = this.dataset.suffix || '';
        this._duration = parseInt(this.dataset.duration, 10) || 1600;
        this._raf = null;

        /* Keep the authored decimal precision ("98.5" → 1 decimal). */
        const decimals = (String(raw).split('.')[1] || '').length;
        this._format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });

        if (!PSSPTheme.motionOK()) {
          this._render(this._target);
          return;
        }

        /* Start from zero so the count-up is visible when reached. */
        this._render(0);
        this._stopObserving = PSSPTheme.onVisible(this, () => this._animate(), '0px');
      }

      disconnectedCallback() {
        if (this._raf) window.cancelAnimationFrame(this._raf);
        this._raf = null;
        if (this._stopObserving) this._stopObserving();
        this._stopObserving = null;
      }

      _animate() {
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / this._duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3); /* easeOutCubic */
          this._render(this._target * eased);
          if (progress < 1) {
            this._raf = window.requestAnimationFrame(tick);
          } else {
            this._raf = null;
            this._render(this._target); /* exact landing value */
          }
        };
        this._raf = window.requestAnimationFrame(tick);
      }

      _render(value) {
        this.textContent = this._prefix + this._format.format(value) + this._suffix;
      }
    }
  );
}

/* ==========================================================================
   8. <quantity-input> — +/- stepper around a number input
   --------------------------------------------------------------------------
   <quantity-input class="qty">
     <button data-qty-minus …> <input type="number" …> <button data-qty-plus …>
   </quantity-input>
   Clamps to min/max/step, dispatches a bubbling 'change' on the input so
   product forms and the cart drawer can react. Minus disabled at min.
   ========================================================================== */

if (!customElements.get('quantity-input')) {
  customElements.define(
    'quantity-input',
    class extends HTMLElement {
      connectedCallback() {
        this._input = this.querySelector('input');
        if (!this._input) return;
        this._minus = this.querySelector('[data-qty-minus]');
        this._plus = this.querySelector('[data-qty-plus]');

        this._onMinus = () => this._stepBy(-1);
        this._onPlus = () => this._stepBy(1);
        this._onChange = () => {
          const clamped = this._clamp(parseFloat(this._input.value));
          if (String(clamped) !== this._input.value) this._input.value = clamped;
          this._syncButtons();
        };

        if (this._minus) this._minus.addEventListener('click', this._onMinus);
        if (this._plus) this._plus.addEventListener('click', this._onPlus);
        this._input.addEventListener('change', this._onChange);

        this._syncButtons();
      }

      disconnectedCallback() {
        if (this._minus && this._onMinus) this._minus.removeEventListener('click', this._onMinus);
        if (this._plus && this._onPlus) this._plus.removeEventListener('click', this._onPlus);
        if (this._input && this._onChange) this._input.removeEventListener('change', this._onChange);
        this._onMinus = null;
        this._onPlus = null;
        this._onChange = null;
      }

      get _min() {
        const v = parseFloat(this._input.min);
        return Number.isFinite(v) ? v : 1;
      }

      get _max() {
        const v = parseFloat(this._input.max);
        return Number.isFinite(v) ? v : Infinity;
      }

      get _stepSize() {
        const v = parseFloat(this._input.step);
        return Number.isFinite(v) && v > 0 ? v : 1;
      }

      /** Snap to the step grid (anchored at min) and clamp to min/max. */
      _clamp(value) {
        if (!Number.isFinite(value)) return this._min;
        const snapped = Math.round((value - this._min) / this._stepSize) * this._stepSize + this._min;
        return Math.min(Math.max(snapped, this._min), this._max);
      }

      _stepBy(direction) {
        const current = parseFloat(this._input.value);
        const base = Number.isFinite(current) ? current : this._min;
        const next = this._clamp(base + direction * this._stepSize);
        if (next === base) {
          this._syncButtons();
          return;
        }
        this._input.value = next;
        this._input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      _syncButtons() {
        const value = parseFloat(this._input.value);
        const current = Number.isFinite(value) ? value : this._min;
        if (this._minus) this._minus.disabled = current <= this._min;
        if (this._plus) this._plus.disabled = current >= this._max;
      }
    }
  );
}

/* ==========================================================================
   9. <details-disclosure> — nav dropdown behavior for <details>/<summary>
   --------------------------------------------------------------------------
   Closes when focus leaves, on Escape (returns focus to summary), and on
   click outside. Mirrors open state onto summary[aria-expanded].
   ========================================================================== */

if (!customElements.get('details-disclosure')) {
  customElements.define(
    'details-disclosure',
    class extends HTMLElement {
      connectedCallback() {
        this._details = this.querySelector('details');
        this._summary = this._details ? this._details.querySelector('summary') : null;
        if (!this._details || !this._summary) return;

        this._summary.setAttribute('aria-expanded', this._details.open ? 'true' : 'false');

        this._onToggle = () => {
          this._summary.setAttribute('aria-expanded', this._details.open ? 'true' : 'false');
        };
        this._details.addEventListener('toggle', this._onToggle);

        /* Close when keyboard focus moves outside the component. */
        this._onFocusout = (event) => {
          const next = event.relatedTarget;
          if (next && this.contains(next)) return;
          /* relatedTarget is null when clicking non-focusable areas — defer
             one frame and re-check where focus actually landed. */
          window.requestAnimationFrame(() => {
            if (!this.contains(document.activeElement)) this._close();
          });
        };
        this.addEventListener('focusout', this._onFocusout);

        this._onKeydown = (event) => {
          if (event.key !== 'Escape' || !this._details.open) return;
          this._close();
          this._summary.focus();
        };
        this.addEventListener('keydown', this._onKeydown);

        /* Click outside closes (covers non-focusable click targets). */
        this._onDocClick = (event) => {
          if (!this._details.open) return;
          if (!this.contains(event.target)) this._close();
        };
        document.addEventListener('click', this._onDocClick);
      }

      disconnectedCallback() {
        if (this._details && this._onToggle) this._details.removeEventListener('toggle', this._onToggle);
        if (this._onFocusout) this.removeEventListener('focusout', this._onFocusout);
        if (this._onKeydown) this.removeEventListener('keydown', this._onKeydown);
        if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
        this._onToggle = null;
        this._onFocusout = null;
        this._onKeydown = null;
        this._onDocClick = null;
      }

      _close() {
        if (this._details) this._details.open = false;
      }
    }
  );
}

/* ==========================================================================
   10. <pssp-drawer> — sliding panel (menu, cart, filters)
   --------------------------------------------------------------------------
   <pssp-drawer id="CartDrawer">
     <div class="drawer__overlay"></div>
     <div class="drawer__panel"> … <button data-drawer-close>…</button> </div>
   </pssp-drawer>
   Openers anywhere in the page: [data-drawer-open="CartDrawer"].
   Open state = .is-open on the element (base.css contract). Focus trapped
   inside the panel; Escape/overlay/close buttons close; body scroll locked;
   'drawer:open' / 'drawer:close' bubble for the header + analytics.
   ========================================================================== */

if (!customElements.get('pssp-drawer')) {
  customElements.define(
    'pssp-drawer',
    class extends HTMLElement {
      connectedCallback() {
        if (!this.id) return; /* id is required for trigger delegation */
        this._overlay = this.querySelector('.drawer__overlay');
        this._panel = this.querySelector('.drawer__panel');
        if (!this._panel) return;

        this._isOpen = this.classList.contains('is-open');
        this._trigger = null;

        if (!this._panel.hasAttribute('role')) this._panel.setAttribute('role', 'dialog');
        this._panel.setAttribute('aria-modal', 'true');

        /* One document-level delegate handles every opener + closer. */
        this._onDocClick = (event) => {
          const opener = event.target.closest(`[data-drawer-open="${this.id}"]`);
          if (opener) {
            event.preventDefault();
            this.open(opener);
            return;
          }
          if (this._isOpen && this.contains(event.target) && event.target.closest('[data-drawer-close]')) {
            event.preventDefault();
            this.close();
          }
        };
        document.addEventListener('click', this._onDocClick);

        if (this._overlay) {
          this._onOverlay = () => this.close();
          this._overlay.addEventListener('click', this._onOverlay);
        }

        /* Escape listener is attached only while open. */
        this._onKeydown = (event) => {
          if (event.key === 'Escape') this.close();
        };
      }

      disconnectedCallback() {
        if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
        if (this._overlay && this._onOverlay) this._overlay.removeEventListener('click', this._onOverlay);
        document.removeEventListener('keydown', this._onKeydown);
        if (this._isOpen) {
          /* Section unloaded while open (editor) — restore page state. */
          PSSPTheme.releaseFocus();
          PSSPTheme.unlockScroll();
          this._isOpen = false;
        }
        this._onDocClick = null;
        this._onOverlay = null;
      }

      /** @param {Element} [trigger] element to restore focus to on close. */
      open(trigger) {
        if (this._isOpen) return;
        this._isOpen = true;
        this._trigger = trigger || document.activeElement;
        if (this._trigger && this._trigger.setAttribute) {
          this._trigger.setAttribute('aria-expanded', 'true');
        }

        this.classList.add('is-open');
        PSSPTheme.lockScroll();
        document.addEventListener('keydown', this._onKeydown);

        /* Wait one frame so the panel is visible before focusing. */
        window.requestAnimationFrame(() => {
          if (this._isOpen) PSSPTheme.trapFocus(this._panel);
        });

        this.dispatchEvent(
          new CustomEvent('drawer:open', { bubbles: true, detail: { id: this.id } })
        );
      }

      close() {
        if (!this._isOpen) return;
        this._isOpen = false;

        this.classList.remove('is-open');
        document.removeEventListener('keydown', this._onKeydown);
        PSSPTheme.releaseFocus();
        PSSPTheme.unlockScroll();

        if (this._trigger && this._trigger.setAttribute) {
          this._trigger.setAttribute('aria-expanded', 'false');
        }
        if (this._trigger && this._trigger.focus && this._trigger.isConnected) {
          this._trigger.focus();
        }
        this._trigger = null;

        this.dispatchEvent(
          new CustomEvent('drawer:close', { bubbles: true, detail: { id: this.id } })
        );
      }
    }
  );
}

/* ==========================================================================
   11. <pssp-modal> — centered dialog (search)
   --------------------------------------------------------------------------
   <pssp-modal id="SearchModal">
     <div class="modal__overlay"></div>
     <div class="modal__panel"> … <button data-modal-close>…</button> </div>
   </pssp-modal>
   Openers: [data-modal-open="SearchModal"]. Same focus/scroll/Escape
   behavior as the drawer; autofocuses the first visible input on open.
   ========================================================================== */

if (!customElements.get('pssp-modal')) {
  customElements.define(
    'pssp-modal',
    class extends HTMLElement {
      connectedCallback() {
        if (!this.id) return;
        this._overlay = this.querySelector('.modal__overlay');
        this._panel = this.querySelector('.modal__panel') || this;
        this._isOpen = this.classList.contains('is-open');
        this._trigger = null;

        if (!this._panel.hasAttribute('role')) this._panel.setAttribute('role', 'dialog');
        this._panel.setAttribute('aria-modal', 'true');

        this._onDocClick = (event) => {
          const opener = event.target.closest(`[data-modal-open="${this.id}"]`);
          if (opener) {
            event.preventDefault();
            this.open(opener);
            return;
          }
          if (this._isOpen && this.contains(event.target) && event.target.closest('[data-modal-close]')) {
            event.preventDefault();
            this.close();
          }
        };
        document.addEventListener('click', this._onDocClick);

        if (this._overlay) {
          this._onOverlay = () => this.close();
          this._overlay.addEventListener('click', this._onOverlay);
        }

        this._onKeydown = (event) => {
          if (event.key === 'Escape') this.close();
        };
      }

      disconnectedCallback() {
        if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
        if (this._overlay && this._onOverlay) this._overlay.removeEventListener('click', this._onOverlay);
        document.removeEventListener('keydown', this._onKeydown);
        if (this._isOpen) {
          PSSPTheme.releaseFocus();
          PSSPTheme.unlockScroll();
          this._isOpen = false;
        }
        this._onDocClick = null;
        this._onOverlay = null;
      }

      open(trigger) {
        if (this._isOpen) return;
        this._isOpen = true;
        this._trigger = trigger || document.activeElement;
        if (this._trigger && this._trigger.setAttribute) {
          this._trigger.setAttribute('aria-expanded', 'true');
        }

        this.classList.add('is-open');
        PSSPTheme.lockScroll();
        document.addEventListener('keydown', this._onKeydown);

        window.requestAnimationFrame(() => {
          if (!this._isOpen) return;
          /* Autofocus the first real input (search field), else default. */
          const input = this._panel.querySelector(
            'input:not([type="hidden"]):not([disabled])'
          );
          PSSPTheme.trapFocus(this._panel, input || undefined);
        });

        this.dispatchEvent(
          new CustomEvent('modal:open', { bubbles: true, detail: { id: this.id } })
        );
      }

      close() {
        if (!this._isOpen) return;
        this._isOpen = false;

        this.classList.remove('is-open');
        document.removeEventListener('keydown', this._onKeydown);
        PSSPTheme.releaseFocus();
        PSSPTheme.unlockScroll();

        if (this._trigger && this._trigger.setAttribute) {
          this._trigger.setAttribute('aria-expanded', 'false');
        }
        if (this._trigger && this._trigger.focus && this._trigger.isConnected) {
          this._trigger.focus();
        }
        this._trigger = null;

        this.dispatchEvent(
          new CustomEvent('modal:close', { bubbles: true, detail: { id: this.id } })
        );
      }
    }
  );
}

/* ==========================================================================
   12. <pssp-header> — sticky / transparent header controller
   --------------------------------------------------------------------------
   Attributes: data-sticky="always" | "on-scroll-up" | "none".
   Transparency is declared by the section via a .header--transparent class
   (on the element or a descendant); this controller only flips state
   classes — base.css draws the actual styles:
     .is-scrolled     scrollY > 24 (solid backdrop kicks in)
     .is-transparent  transparent variant while at the top
     .is-hidden       on-scroll-up mode, scrolling down past 240px
   Also maintains --header-height on :root via ResizeObserver.
   ========================================================================== */

if (!customElements.get('pssp-header')) {
  customElements.define(
    'pssp-header',
    class extends HTMLElement {
      connectedCallback() {
        this._sticky = this.dataset.sticky || 'none';
        this._transparent =
          this.classList.contains('header--transparent') ||
          !!this.querySelector('.header--transparent');
        this._lastY = window.scrollY;
        this._openLayers = 0;
        this._raf = null;

        /* Publish the real header height for anchor offsets etc. */
        if ('ResizeObserver' in window) {
          this._ro = new ResizeObserver((entries) => {
            const entry = entries[entries.length - 1];
            if (!entry) return;
            const height = Math.ceil(entry.contentRect.height);
            document.documentElement.style.setProperty('--header-height', `${height}px`);
          });
          this._ro.observe(this);
        } else {
          document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
        }

        /* rAF-throttled scroll handler. */
        this._onScroll = () => {
          if (this._raf) return;
          this._raf = window.requestAnimationFrame(() => {
            this._raf = null;
            this._applyScrollState();
          });
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });

        /* Never hide while a drawer or modal is open. */
        this._onLayerOpen = () => {
          this._openLayers += 1;
          this.classList.remove('is-hidden');
        };
        this._onLayerClose = () => {
          this._openLayers = Math.max(0, this._openLayers - 1);
        };
        document.addEventListener('drawer:open', this._onLayerOpen);
        document.addEventListener('drawer:close', this._onLayerClose);
        document.addEventListener('modal:open', this._onLayerOpen);
        document.addEventListener('modal:close', this._onLayerClose);

        /* Never hide while keyboard focus is inside the header. */
        this._onFocusin = () => this.classList.remove('is-hidden');
        this.addEventListener('focusin', this._onFocusin);

        this._applyScrollState();
      }

      disconnectedCallback() {
        if (this._ro) this._ro.disconnect();
        if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
        if (this._raf) window.cancelAnimationFrame(this._raf);
        document.removeEventListener('drawer:open', this._onLayerOpen);
        document.removeEventListener('drawer:close', this._onLayerClose);
        document.removeEventListener('modal:open', this._onLayerOpen);
        document.removeEventListener('modal:close', this._onLayerClose);
        if (this._onFocusin) this.removeEventListener('focusin', this._onFocusin);
        this._ro = null;
        this._onScroll = null;
        this._raf = null;
        this._onLayerOpen = null;
        this._onLayerClose = null;
        this._onFocusin = null;
      }

      _applyScrollState() {
        const y = window.scrollY;

        this.classList.toggle('is-scrolled', y > 24);
        if (this._transparent) {
          this.classList.toggle('is-transparent', y <= 24);
        }

        if (this._sticky === 'on-scroll-up') {
          const delta = y - this._lastY;
          /* Ignore sub-4px jitter (elastic scroll, scrollbar snaps). */
          if (Math.abs(delta) > 4) {
            const blocked =
              this._openLayers > 0 || this.contains(document.activeElement);
            if (delta > 0 && y > 240 && !blocked) {
              this.classList.add('is-hidden');
            } else if (delta < 0) {
              this.classList.remove('is-hidden');
            }
            this._lastY = y;
          }
        } else {
          this._lastY = y;
        }
      }
    }
  );
}

/* ==========================================================================
   13. Magnetic buttons
   --------------------------------------------------------------------------
   [data-magnetic] elements ease up to 6px toward the pointer. Only active
   when body[data-magnetic="true"], on fine pointers, with motion allowed —
   otherwise this whole controller is skipped (zero listeners).
   Binding is lazy: a single document pointerover delegate attaches
   move/leave handlers to each element the first time it is hovered, so
   editor-injected sections work without rescans (WeakSet-tracked).
   ========================================================================== */

if (!PSSPTheme.magnetic) {
  PSSPTheme.magnetic = {
    _bound: null,
    _onOver: null,

    init() {
      if (this._onOver) return; /* already initialized */
      if (
        document.body.dataset.magnetic !== 'true' ||
        !PSSPTheme.pointerFine() ||
        !PSSPTheme.motionOK()
      ) {
        return;
      }

      this._bound = new WeakSet();
      this._onOver = (event) => {
        const el = event.target.closest ? event.target.closest('[data-magnetic]') : null;
        if (!el || this._bound.has(el)) return;
        this._bound.add(el);
        this._attach(el);
      };
      document.addEventListener('pointerover', this._onOver);
    },

    _attach(el) {
      let raf = null;
      let lastEvent = null;
      const maxShift = 6; /* px */

      const onMove = (event) => {
        lastEvent = event;
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = null;
          if (!lastEvent || !PSSPTheme.motionOK()) return;
          const rect = el.getBoundingClientRect();
          const nx = (lastEvent.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
          const ny = (lastEvent.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
          const cx = Math.max(-1, Math.min(1, nx)) * maxShift;
          const cy = Math.max(-1, Math.min(1, ny)) * maxShift;
          el.style.transform = `translate3d(${cx.toFixed(1)}px, ${cy.toFixed(1)}px, 0)`;
        });
      };

      const onLeave = () => {
        if (raf) {
          window.cancelAnimationFrame(raf);
          raf = null;
        }
        lastEvent = null;
        el.style.transform = '';
      };

      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      /* Listeners are GC'd with the element; WeakSet holds no strong ref. */
    }
  };
}

/* ==========================================================================
   14. Cursor glow
   --------------------------------------------------------------------------
   A single fixed div.cursor-glow (styled in base.css: blurred radial accent,
   pointer-events none) follows the pointer via rAF. .is-active only while
   the pointer is over dark sections (.scheme--carbon / .scheme--graphite).
   Gated hard: body[data-cursor-glow="true"] AND perf === "full" AND fine
   pointer AND motion OK. Removed on pagehide.
   ========================================================================== */

if (!PSSPTheme.cursorGlow) {
  PSSPTheme.cursorGlow = {
    _el: null,
    _raf: null,
    _x: 0,
    _y: 0,
    _overDark: false,
    _onMove: null,
    _onPageHide: null,

    init() {
      if (this._el) return; /* already active */
      if (
        document.body.dataset.cursorGlow !== 'true' ||
        PSSPTheme.perf() !== 'full' ||
        !PSSPTheme.pointerFine() ||
        !PSSPTheme.motionOK()
      ) {
        return;
      }

      const el = document.createElement('div');
      el.className = 'cursor-glow';
      el.setAttribute('aria-hidden', 'true');
      document.body.appendChild(el);
      this._el = el;

      this._onMove = (event) => {
        this._x = event.clientX;
        this._y = event.clientY;
        this._overDark = !!(
          event.target &&
          event.target.closest &&
          event.target.closest('.scheme--carbon, .scheme--graphite')
        );
        if (this._raf) return;
        this._raf = window.requestAnimationFrame(() => {
          this._raf = null;
          if (!this._el) return;
          this._el.style.transform = `translate3d(${this._x}px, ${this._y}px, 0)`;
          this._el.classList.toggle('is-active', this._overDark);
        });
      };
      document.addEventListener('pointermove', this._onMove, { passive: true });

      this._onPageHide = () => this.destroy();
      window.addEventListener('pagehide', this._onPageHide);
    },

    destroy() {
      if (this._onMove) document.removeEventListener('pointermove', this._onMove);
      if (this._onPageHide) window.removeEventListener('pagehide', this._onPageHide);
      if (this._raf) window.cancelAnimationFrame(this._raf);
      if (this._el && this._el.parentNode) this._el.parentNode.removeChild(this._el);
      this._el = null;
      this._raf = null;
      this._onMove = null;
      this._onPageHide = null;
    }
  };
}

/* ==========================================================================
   15. Boot + Shopify theme editor lifecycle
   --------------------------------------------------------------------------
   Custom elements re-upgrade automatically when the editor re-renders a
   section; only the document-level controllers (reveal, parallax) need a
   rescan on shopify:section:load. A live prefers-reduced-motion change
   reveals everything and tears down the parallax loop immediately.
   ========================================================================== */

if (!PSSPTheme.init) {
  PSSPTheme.init = function init() {
    if (PSSPTheme._booted) return;
    PSSPTheme._booted = true;

    PSSPTheme.reveal.scan();
    PSSPTheme.parallax.scan();
    PSSPTheme.magnetic.init();
    PSSPTheme.cursorGlow.init();

    /* Theme editor: rescan document-level controllers for new markup. */
    document.addEventListener('shopify:section:load', () => {
      PSSPTheme.reveal.scan();
      PSSPTheme.parallax.scan();
    });

    /* React to a live OS-level reduced-motion switch. */
    const onMotionChange = () => {
      if (PSSPTheme.prefersReducedMotion()) {
        PSSPTheme.reveal.revealAll();
      } else {
        PSSPTheme.reveal.scan();
      }
      PSSPTheme.parallax.scan();
    };
    if (typeof PSSPTheme._mqReduced.addEventListener === 'function') {
      PSSPTheme._mqReduced.addEventListener('change', onMotionChange);
    }
  };
}

/* global.js is loaded with defer, so the DOM is parsed by the time this
   runs — but guard against both ready states for safety. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => PSSPTheme.init());
} else {
  PSSPTheme.init();
}
